import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getProfile, claimProfile, trackChallengeEvent, applyReferral, getReferralInfo } from '../services/api';
import { track } from '../services/track';
import { firePixelEvent } from '../pixels';
import { ArchetypeIcon } from './NeuralProfile';
import { useAuth } from '../contexts/AuthContext';

function useCopy(text, ms = 2000) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), ms);
    });
  };
  return [copied, copy];
}

const DIMENSION_NAMES = {
  rawSpeed:         'Raw Speed',
  decisionQuality:  'Decision',
  aimPrecision:     'Aim',
  trackingAccuracy: 'Tracking',
  workingMemory:    'Memory',
  consistency:      'Consistency',
  flickSpeed:       'Flick Speed',
  prioritization:   'Prioritization',
};

function CountUp({ target, color }) {
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!target) return;
    let frame;
    const start = performance.now();
    const duration = 1200;
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(eased * target));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target]);

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontSize: 80, fontWeight: 900, lineHeight: 1,
        color, fontVariantNumeric: 'tabular-nums',
        textShadow: `0 0 48px ${color}88`,
        letterSpacing: '-0.04em',
      }}>
        {val}
      </div>
      <div style={{ fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--text-muted)', marginTop: 6 }}>
        Overall Score
      </div>
    </div>
  );
}

export default function CombineComplete() {
  const { state } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Combine Complete | NeuroRank';
    track('report_gate_view', { shareableId: state?.shareableId, archetype: state?.archetype?.id });
    return () => { document.title = 'NeuroRank | Cognitive Combine'; };
  }, []); // eslint-disable-line
  useEffect(() => {
    if (!state?.shareableId) navigate('/', { replace: true });
  }, [state, navigate]);
  useEffect(() => {
    if (state?.shareableId && state?.scores && state?.archetype) {
      const utm = JSON.parse(sessionStorage.getItem('nr_utm') || '{}');
      firePixelEvent('ViewContent', { content_name: 'combine_completed' });
      window.posthog?.capture('combine_completed', {
        shareableId: state.shareableId,
        archetype: state.archetype?.id,
        overallScore: state.scores?.overallScore,
        genre: state.genre,
        ...utm,
      });
    }
  }, []); // eslint-disable-line
  useEffect(() => {
    const challengerId = sessionStorage.getItem('nr_challenge_id');
    if (challengerId) {
      sessionStorage.removeItem('nr_challenge_id');
      getProfile(challengerId).then(ch => {
        setChallenger(ch);
        trackChallengeEvent('complete', challengerId, state?.shareableId);
      }).catch(() => {});
    }
  }, []);

  if (!state?.shareableId) return null;

  const { shareableId, scores, archetype } = state;
  const profileUrl = `${window.location.origin}/profile/${shareableId}`;

  const overall = scores.overallScore ?? Math.round(
    Object.entries(scores)
      .filter(([k]) => k !== 'overallScore')
      .map(([, v]) => v)
      .reduce((a, b) => a + b, 0) /
    Object.entries(scores).filter(([k]) => k !== 'overallScore').length
  );

  const topDims = Object.entries(scores)
    .filter(([k]) => k !== 'overallScore')
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3);

  const archetypeColor = archetype.color || '#00d4ff';
  const archetypeGlow  = archetype.glow  || 'rgba(0,212,255,0.3)';

  const topDimName = topDims[0] ? (DIMENSION_NAMES[topDims[0][0]] || topDims[0][0]) : null;
  const topDimScore = topDims[0] ? topDims[0][1] : null;
  const tweetText = topDimName
    ? `${archetype.name}. ${overall}/100 on @NeuroRankApp.\n\n${topDimName}: ${topDimScore}. Think you can top it?`
    : `${archetype.name} — ${overall}/100 on @NeuroRankApp. What's your neural profile?`;
  const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(profileUrl)}`;
  const [linkCopied, copyLink] = useCopy(profileUrl);
  const [challenger, setChallenger] = useState(null);

  const { user, register, refreshUser } = useAuth();
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regTag, setRegTag] = useState('');
  const [regState, setRegState] = useState('idle'); // idle | saving | done | error
  const [regError, setRegError] = useState('');
  const [reportTeaser, setReportTeaser] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [refLinkCopied, setRefLinkCopied] = useState(false);

  useEffect(() => {
    if (!state?.shareableId || user) return;
    getProfile(state.shareableId)
      .then(p => {
        if (p?.report) {
          // First sentence only — enough to hook, not enough to satisfy
          const first = p.report.split(/(?<=[.!?])\s+/)[0] || '';
          setReportTeaser(first);
        }
      })
      .catch(() => {});
  }, [state?.shareableId, user]); // eslint-disable-line

  const genreLabel = state.genre
    ? `${state.genre.toUpperCase()} COMBINE`
    : 'COMBINE COMPLETE';

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-primary)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 24px',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Radial glow */}
      <div style={{
        position: 'absolute', top: '45%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 700, height: 700, borderRadius: '50%',
        background: `radial-gradient(circle, ${archetypeGlow} 0%, transparent 65%)`,
        pointerEvents: 'none',
      }} />

      {/* Subtle grid */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.012) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.012) 1px, transparent 1px)',
        backgroundSize: '44px 44px',
        pointerEvents: 'none',
      }} />

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 1, maxWidth: 540, width: '100%' }}>

        {/* Eyebrow */}
        <motion.p
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4 }}
          style={{
            fontSize: 10, letterSpacing: 4, textTransform: 'uppercase',
            color: archetypeColor, fontWeight: 700, marginBottom: 40,
          }}
        >
          {genreLabel}
        </motion.p>

        {/* Archetype icon */}
        <motion.div
          initial={{ opacity: 0, scale: 0.4 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          style={{
            marginBottom: 28, lineHeight: 1,
            filter: `drop-shadow(0 0 28px ${archetypeColor}aa)`,
          }}
        >
          <ArchetypeIcon archetypeId={archetype.id} size={72} color={archetypeColor} />
        </motion.div>

        {/* Archetype label + name */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
        >
          <p style={{
            fontSize: 10, letterSpacing: 3, textTransform: 'uppercase',
            color: 'var(--text-muted)', fontWeight: 700, marginBottom: 10,
          }}>
            Your Archetype
          </p>
          <h1 style={{
            fontSize: 42, fontWeight: 900, letterSpacing: '-0.03em',
            color: 'var(--text-primary)', margin: 0, lineHeight: 1.1,
          }}>
            {archetype.name}
          </h1>
          <p style={{
            fontSize: 15, color: archetypeColor, fontStyle: 'italic',
            marginTop: 10, marginBottom: 36, opacity: 0.9,
          }}>
            "{archetype.tagline}"
          </p>
        </motion.div>

        {/* Divider */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 1.1, duration: 0.5, ease: 'easeOut' }}
          style={{
            height: 1, background: `linear-gradient(90deg, transparent, ${archetypeColor}66, transparent)`,
            marginBottom: 36,
          }}
        />

        {/* Score */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.4 }}
          style={{ marginBottom: 32 }}
        >
          <CountUp target={overall} color={archetypeColor} />
        </motion.div>

        {/* Top 3 dims */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6, duration: 0.4 }}
          style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 44 }}
        >
          {topDims.map(([key, value]) => (
            <div
              key={key}
              style={{
                padding: '7px 16px', borderRadius: 20,
                border: `1px solid ${archetypeColor}44`,
                background: `${archetypeColor}0d`,
                fontSize: 12, fontWeight: 700,
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <span style={{ color: archetypeColor, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
              <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                {DIMENSION_NAMES[key] || key}
              </span>
            </div>
          ))}
        </motion.div>

        {/* Challenge result */}
        {challenger && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.5, duration: 0.4 }}
            style={{
              marginBottom: 28, padding: '16px 20px', borderRadius: 12,
              border: `1px solid ${overall >= (challenger.scores?.overallScore ?? 0) ? '#10b981' : '#ef4444'}55`,
              background: overall >= (challenger.scores?.overallScore ?? 0) ? '#10b98109' : '#ef444409',
            }}
          >
            {overall >= (challenger.scores?.overallScore ?? 0) ? (
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#10b981' }}>
                🏆 You beat the challenge! {overall} vs {challenger.scores?.overallScore ?? '?'} — {challenger.playerTag}
              </p>
            ) : (
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#ef4444' }}>
                ⚔ Close! {overall} vs {challenger.scores?.overallScore ?? '?'} — {challenger.playerTag} wins this round
              </p>
            )}
            <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
              <a href={`/profile/${challenger.shareableId}`} style={{ color: 'var(--accent)', textDecoration: 'none' }}>
                View their profile →
              </a>
            </p>
          </motion.div>
        )}

        {/* Share buttons */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.85, duration: 0.4 }}
          style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 24 }}
        >
          <a
            href={tweetUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => window.posthog?.capture('profile_shared', {
              platform: 'twitter',
              shareableId,
              archetype: archetype?.id,
              overallScore: overall,
            })}
            style={{
              padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700,
              color: '#1d9bf0', border: '1px solid #1d9bf044', background: '#1d9bf011',
              textDecoration: 'none', transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#1d9bf022'}
            onMouseLeave={e => e.currentTarget.style.background = '#1d9bf011'}
          >
            Share on X
          </a>
          <button
            onClick={copyLink}
            style={{
              padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700,
              color: linkCopied ? archetypeColor : 'var(--text-secondary)',
              border: `1px solid ${linkCopied ? archetypeColor + '66' : 'var(--border)'}`,
              background: linkCopied ? archetypeColor + '11' : 'transparent',
              cursor: 'pointer', transition: 'all 0.15s',
            }}
          >
            {linkCopied ? '✓ Copied!' : 'Copy Link'}
          </button>
        </motion.div>

        {/* Save profile — account creation or already logged in */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.0, duration: 0.45 }}
          style={{ marginBottom: 28 }}
        >
          {user ? (
            /* Already logged in — just claim and go */
            regState === 'done' ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
                <p style={{ fontSize: 13, color: '#10b981', fontWeight: 600, margin: 0 }}>
                  ✓ Profile saved to your account
                </p>
                <button
                  className="btn btn--primary"
                  style={{ fontSize: 15, padding: '13px 52px', fontWeight: 800 }}
                  onClick={() => navigate(`/profile/${shareableId}`, { replace: true })}
                >
                  View Full Profile →
                </button>
              </div>
            ) : (
              <button
                onClick={async () => {
                  setRegState('saving');
                  try {
                    await claimProfile(shareableId);
                    await refreshUser();
                    setRegState('done');
                  } catch {
                    setRegState('idle');
                  }
                }}
                disabled={regState === 'saving'}
                style={{
                  width: '100%', padding: '12px', borderRadius: 10, fontSize: 13, fontWeight: 700,
                  background: archetypeColor + '22', border: `1px solid ${archetypeColor}55`,
                  color: archetypeColor, cursor: 'pointer',
                }}
              >
                {regState === 'saving' ? 'Saving…' : 'Save profile to my account'}
              </button>
            )
          ) : regState === 'done' ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
              <p style={{ fontSize: 13, color: '#10b981', fontWeight: 600, margin: 0 }}>
                ✓ Profile saved — you're in
              </p>
              <button
                className="btn btn--primary"
                style={{ fontSize: 15, padding: '13px 52px', fontWeight: 800 }}
                onClick={() => navigate(`/profile/${shareableId}`, { replace: true })}
              >
                View Full Profile →
              </button>
              {referralCode && (
                <div style={{
                  marginTop: 8, padding: '14px 16px', borderRadius: 10,
                  background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.2)',
                  width: '100%', textAlign: 'center',
                }}>
                  <p style={{ margin: '0 0 6px', fontSize: 13, color: 'var(--text-secondary)' }}>
                    Refer a friend — they test free, you get a free re-test when they complete it.
                  </p>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/?ref=${referralCode}`);
                      setRefLinkCopied(true);
                      setTimeout(() => setRefLinkCopied(false), 2000);
                    }}
                    style={{
                      padding: '7px 18px', borderRadius: 7, fontSize: 12, fontWeight: 700,
                      background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.3)',
                      color: '#00d4ff', cursor: 'pointer',
                    }}
                  >
                    {refLinkCopied ? '✓ Copied' : 'Copy referral link'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div style={{
              border: `1px solid ${archetypeColor}33`,
              borderRadius: 12,
              padding: '20px 20px 16px',
              background: `${archetypeColor}08`,
            }}>
              <p style={{
                margin: '0 0 10px', fontSize: 15, fontWeight: 800,
                color: 'var(--text-primary)', textAlign: 'left',
              }}>
                Unlock your AI scouting report
              </p>

              {/* Report teaser — first sentence, blurred remainder */}
              {reportTeaser && (
                <div style={{ position: 'relative', marginBottom: 16, overflow: 'hidden' }}>
                  <p style={{
                    margin: 0, fontSize: 12, color: 'var(--text-secondary)',
                    lineHeight: 1.7, textAlign: 'left',
                    borderLeft: `2px solid ${archetypeColor}66`,
                    paddingLeft: 10,
                  }}>
                    {reportTeaser}
                  </p>
                  <div style={{
                    fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.7,
                    filter: 'blur(4px)', userSelect: 'none', textAlign: 'left',
                    paddingLeft: 10, borderLeft: `2px solid ${archetypeColor}33`,
                    marginTop: 6,
                  }}>
                    Your cognitive profile reveals a clear performance pattern that explains both your strengths under pressure and the specific conditions where your decision quality degrades. The full analysis covers…
                  </div>
                </div>
              )}

              <p style={{
                margin: '0 0 14px', fontSize: 11, color: 'var(--text-muted)', textAlign: 'left', lineHeight: 1.6,
              }}>
                Free account · track your scores over time · re-test every 24h
              </p>

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!regEmail.trim() || !regPassword.trim()) return;
                  track('report_gate_cta', { archetype: state.archetype?.id, overallScore: state.scores?.overallScore });
                  setRegState('saving');
                  setRegError('');
                  try {
                    const newUser = await register(regEmail.trim(), regPassword, regTag.trim() || undefined);
                    await claimProfile(shareableId);
                    await refreshUser();
                    // Apply referral if present
                    const refCode = localStorage.getItem('nr_ref');
                    if (refCode) {
                      applyReferral(refCode).catch(() => {});
                      localStorage.removeItem('nr_ref');
                    }
                    // Fetch referral code to show share card
                    getReferralInfo().then(info => { if (info?.referralCode) setReferralCode(info.referralCode); }).catch(() => {});
                    const utm = JSON.parse(sessionStorage.getItem('nr_utm') || '{}');
                    try {
                      firePixelEvent('Lead', { content_name: 'account_created' });
                      if (newUser?.id) window.posthog?.identify(newUser.id, { email: regEmail.trim(), ...utm });
                      window.posthog?.capture('account_created', {
                        shareableId,
                        archetype: state.archetype?.id,
                        overallScore: state.scores?.overallScore,
                        ...utm,
                      });
                    } catch {}
                    sessionStorage.removeItem('nr_utm');
                    setRegState('done');
                  } catch (err) {
                    setRegError(err?.response?.data?.error || 'Something went wrong');
                    setRegState('error');
                  }
                }}
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              >
                <input
                  type="text"
                  placeholder="Player tag (optional)"
                  value={regTag}
                  onChange={e => setRegTag(e.target.value)}
                  disabled={regState === 'saving'}
                  style={{
                    padding: '9px 12px', borderRadius: 8, fontSize: 13,
                    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
                    color: 'var(--text-primary)', outline: 'none',
                  }}
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={regEmail}
                  onChange={e => { setRegEmail(e.target.value); if (regState === 'error') setRegState('idle'); }}
                  disabled={regState === 'saving'}
                  required
                  style={{
                    padding: '9px 12px', borderRadius: 8, fontSize: 13,
                    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
                    color: 'var(--text-primary)', outline: 'none',
                  }}
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={regPassword}
                  onChange={e => { setRegPassword(e.target.value); if (regState === 'error') setRegState('idle'); }}
                  disabled={regState === 'saving'}
                  required
                  style={{
                    padding: '9px 12px', borderRadius: 8, fontSize: 13,
                    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
                    color: 'var(--text-primary)', outline: 'none',
                  }}
                />
                {regError && (
                  <p style={{ margin: 0, fontSize: 11, color: '#ef4444', textAlign: 'left' }}>{regError}</p>
                )}
                <button
                  type="submit"
                  disabled={regState === 'saving' || !regEmail.trim() || !regPassword.trim()}
                  style={{
                    padding: '11px', borderRadius: 8, fontSize: 13, fontWeight: 800,
                    background: archetypeColor, border: 'none',
                    color: '#000', cursor: 'pointer', marginTop: 4,
                    opacity: (regState === 'saving' || !regEmail.trim() || !regPassword.trim()) ? 0.6 : 1,
                  }}
                >
                  {regState === 'saving' ? 'Creating account…' : 'Unlock Full Report — Free'}
                </button>
              </form>

              <button
                onClick={() => navigate(`/profile/${shareableId}`, { replace: true })}
                style={{
                  background: 'none', border: 'none', color: 'var(--text-muted)',
                  fontSize: 11, cursor: 'pointer', marginTop: 12, padding: 0,
                  textDecoration: 'underline', opacity: 0.6,
                }}
              >
                Skip — view profile without report
              </button>
            </div>
          )}
        </motion.div>

        {/* Upsells — shown after profile is created */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.5, duration: 0.5 }}
          style={{ marginTop: 28, display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}
        >
          <a
            href="https://discord.gg/W9Ftc6TC8J"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: '10px 18px', borderRadius: 10, fontSize: 12, fontWeight: 600,
              background: 'rgba(88,101,242,0.08)', border: '1px solid rgba(88,101,242,0.25)',
              color: '#5865F2', cursor: 'pointer', transition: 'background 0.15s',
              textDecoration: 'none', display: 'inline-block',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(88,101,242,0.15)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(88,101,242,0.08)'}
          >
            Compare your archetype with other players → Join the Discord
          </a>
          <button
            onClick={() => navigate('/pricing#boosts')}
            style={{
              padding: '10px 18px', borderRadius: 10, fontSize: 12, fontWeight: 600,
              background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)',
              color: '#f59e0b', cursor: 'pointer', transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(245,158,11,0.15)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(245,158,11,0.08)'}
          >
            ★ Feature Your Profile in Scout Searches — $4.99
          </button>
          <button
            onClick={() => navigate('/pricing#boosts')}
            style={{
              padding: '10px 18px', borderRadius: 10, fontSize: 12, fontWeight: 600,
              background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)',
              color: '#10b981', cursor: 'pointer', transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(16,185,129,0.15)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(16,185,129,0.08)'}
          >
            ↻ Skip 24h Cooldown — Retake Now for $1.99
          </button>
        </motion.div>

      </div>
    </div>
  );
}
